import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./Report.css";

const emptyMonitoring = {
  generatedAt: null,
  range: { label: "Last 7 days" },
  kpiCards: [],
  trend: [],
  outcomeData: [],
  agentHealth: [],
  latencyBreakdown: [],
  alerts: [],
  sessions: [],
};

const previewMonitoring = {
  generatedAt: null,
  range: { label: "Last 7 days" },
  kpiCards: [
    { label: "Sessions", value: "42", change: "+8 today", tone: "good" },
    { label: "AI handled", value: "31", change: "74% contained", tone: "good" },
    { label: "Containment", value: "74%", change: "+6 pts", tone: "good" },
    { label: "Transfers", value: "11", change: "Human assist", tone: "neutral" },
    { label: "Avg latency", value: "1.8s", change: "Stable", tone: "good" },
    { label: "Alerts", value: "1", change: "Review", tone: "warn" },
  ],
  trend: [
    { time: "9 AM", sessions: 4, aiHandled: 3 },
    { time: "10 AM", sessions: 7, aiHandled: 5 },
    { time: "11 AM", sessions: 6, aiHandled: 5 },
    { time: "12 PM", sessions: 9, aiHandled: 7 },
    { time: "1 PM", sessions: 8, aiHandled: 6 },
    { time: "2 PM", sessions: 8, aiHandled: 5 },
  ],
  outcomeData: [
    { name: "AI handled", value: 31, percentLabel: "74%", color: "#ff8a50" },
    { name: "Human transfer", value: 11, percentLabel: "26%", color: "#ffd28a" },
  ],
  agentHealth: [
    {
      id: "insurance-fnol-claims",
      name: "Insurance - FNOL & Claims Mgmt Agent",
      status: "Healthy",
      successRate: "74%",
      latency: "1.8s",
      volume: 42,
    },
    {
      id: "insurance-fnol-claims-assist",
      name: "Insurance - FNOL & Claims Mgmt - Agent Assist",
      status: "Review",
      successRate: "68%",
      latency: "2.1s",
      volume: 18,
    },
  ],
  latencyBreakdown: [
    { component: "LLM", p50: 2300, p90: 4600, p95: 9700, p99: 11900 },
    { component: "Tool", p50: 3000, p90: 28500, p95: 30000, p99: 30000 },
  ],
  alerts: [
    { severity: "Medium", message: "Review transferred sessions for coaching", time: "Today" },
  ],
  sessions: [
    { id: "SES-1042", agent: "FNOL Agent", state: "AI handled" },
    { id: "SES-1041", agent: "Agent Assist", state: "Transferred" },
    { id: "SES-1040", agent: "FNOL Agent", state: "Resolved" },
  ],
};

const fallbackAgents = [
  {
    id: "insurance-fnol-claims",
    name: "Insurance - FNOL & Claims Mgmt Agent",
  },
  {
    id: "insurance-fnol-claims-assist",
    name: "Insurance - FNOL & Claims Mgmt - Agent Assist",
  },
];

const cxStudioSubAgents = [
  {
    id: "document-agent",
    name: "Document Agent",
    tool: "document_intake_service",
    status: "Active",
    successRate: "99%",
    latency: "940ms",
    volume: 9,
  },
  {
    id: "policy-checker-agent",
    name: "Policy Checker Agent",
    tool: "analyze_cgl_policy",
    status: "Active",
    successRate: "98%",
    latency: "1120ms",
    volume: 7,
  },
  {
    id: "coverage-checker-update-agent",
    name: "Coverage Checker Update Agent",
    tool: "coverage-validation",
    status: "Active",
    successRate: "97%",
    latency: "1180ms",
    volume: 6,
  },
  {
    id: "liability-checker-update-agent",
    name: "Liability Checker Update Agent",
    tool: "claim-liability-agent-tool",
    status: "Active",
    successRate: "96%",
    latency: "1210ms",
    volume: 6,
  },
  {
    id: "litigation-checker-update-agent",
    name: "Litigation Checker Update Agent",
    tool: "claim_agent_insights_fetch",
    status: "Active",
    successRate: "95%",
    latency: "1320ms",
    volume: 5,
  },
  {
    id: "surveillance-video-agent",
    name: "Surveillance Video Agent",
    tool: "analyze_claim_video",
    status: "Active",
    successRate: "97%",
    latency: "1450ms",
    volume: 5,
  },
  {
    id: "exposure-analyzer-agent",
    name: "Exposure Analyzer Agent",
    tool: "analyze_demand_letter",
    status: "Active",
    successRate: "96%",
    latency: "1240ms",
    volume: 5,
  },
  {
    id: "witness-statement-agent",
    name: "Witness Statement Agent",
    tool: "reviewWitnessStatement",
    status: "Active",
    successRate: "98%",
    latency: "890ms",
    volume: 4,
  },
  {
    id: "medclaim-insight-extractor-agent",
    name: "MedClaim Insight Extractor Agent",
    tool: "medical_records_reviewer",
    status: "Active",
    successRate: "97%",
    latency: "1540ms",
    volume: 4,
  },
  {
    id: "prior-loss-analyzer",
    name: "Prior Loss Analyzer",
    tool: "analyze_prior_loss",
    status: "Active",
    successRate: "96%",
    latency: "1010ms",
    volume: 3,
  },
  {
    id: "notice-of-loss-agent",
    name: "Notice of Loss Agent",
    tool: "review_notice_of_loss",
    status: "Active",
    successRate: "99%",
    latency: "860ms",
    volume: 3,
  },
  {
    id: "contract-analyzer-agent",
    name: "Contract Analyzer Agent",
    tool: "analyze_lease_contract",
    status: "Active",
    successRate: "96%",
    latency: "1290ms",
    volume: 3,
  },
  {
    id: "lease-contract-claims-agent",
    name: "Lease Contract Claims Agent",
    tool: "lease_contract_document_review",
    status: "Active",
    successRate: "95%",
    latency: "1410ms",
    volume: 3,
  },
  {
    id: "legal-record-agent",
    name: "Legal Record Agent",
    tool: "legal_claim_review",
    status: "Active",
    successRate: "96%",
    latency: "1360ms",
    volume: 2,
  },
  {
    id: "subrogation-agent",
    name: "Subrogation Agent",
    tool: "subrogation_review",
    status: "Active",
    successRate: "97%",
    latency: "980ms",
    volume: 2,
  },
  {
    id: "siu-agent",
    name: "SIU Agent",
    tool: "siu_referral_review",
    status: "Active",
    successRate: "95%",
    latency: "1250ms",
    volume: 2,
  },
];

const CX_STUDIO_DASHBOARD_URL =
  "https://ces.cloud.google.com/projects/gch-us-poc-dtai-gcp-practice/locations/us/apps/5a8c77fe-bedd-4389-bad0-5ab158b93cb7/monitor/dashboard/virtual_agent_dashboard";

const statusClass = (value = "") => value.toLowerCase().replace(/\s+/g, "-");

const latencyMetricColors = {
  p50: "#4c8bf5",
  p90: "#a463f2",
  p95: "#45bf66",
  p99: "#fbbc04",
};

const latencyMetrics = ["p50", "p90", "p95", "p99"];

const formatLatency = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return value || "--";
  if (numericValue >= 1000) return `${(numericValue / 1000).toFixed(1)}s`;
  return `${Math.round(numericValue)}ms`;
};

const normalizeLatencyBreakdown = (rows = []) =>
  rows
    .map((row) => {
      const component = row.component || row.name || row.type || "";

      return {
        component,
        p50: Number(row.p50) || 0,
        p90: Number(row.p90) || 0,
        p95: Number(row.p95) || 0,
        p99: Number(row.p99) || 0,
      };
    })
    .filter((row) => row.component && latencyMetrics.some((metric) => row[metric] > 0));

const formatUpdatedAt = (value) => {
  if (!value) return "No live sessions yet";
  return `Updated ${new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const getApiBaseCandidates = () => {
  const publicApiUrl = process.env.REACT_APP_CHAT_API_URL?.replace(/\/$/, "");
  const apiPorts = [
    process.env.REACT_APP_CHAT_API_PORT,
    "5000",
    "5001",
  ].filter(Boolean);
  const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const bases = [];

  if (isLocalHost) {
    apiPorts.forEach((apiPort) => {
      bases.push(`${window.location.protocol}//localhost:${apiPort}`);
    });
  }

  if (window.location?.hostname && window.location.hostname !== "localhost") {
    apiPorts.forEach((apiPort) => {
      bases.push(`${window.location.protocol}//${window.location.hostname}:${apiPort}`);
    });
  }

  bases.push("");
  if (publicApiUrl) bases.push(publicApiUrl);

  return [...new Set(bases)];
};

async function fetchFirstJson(path, options) {
  let lastError = null;

  for (const base of getApiBaseCandidates()) {
    try {
      const response = await fetch(`${base}${path}`, options);
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.details || data.error || "Request failed");
      }

      return data;
    } catch (error) {
      if (error.name === "AbortError") throw error;
      lastError = error;
    }
  }

  throw lastError || new Error("Request failed");
}

const MonitoringDashboard = ({
  defaultAgentId = "all",
  lockAgent = false,
  hideAgentSelector = false,
  ariaLabel = "Analytics",
  className = "",
}) => {
  const [range, setRange] = useState("7d");
  const [selectedAgentId, setSelectedAgentId] = useState(defaultAgentId);
  const [agentOptions, setAgentOptions] = useState(fallbackAgents);
  const [refreshKey, setRefreshKey] = useState(0);
  const [monitoring, setMonitoring] = useState(emptyMonitoring);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let isMounted = true;

    fetchFirstJson("/api/chat/agents")
      .then((data) => {
        if (!isMounted) return;
        const agents = Array.isArray(data?.agents) ? data.agents : [];
        setAgentOptions(
          agents.length
            ? agents.map((agent) => ({ id: agent.id, name: agent.name }))
            : fallbackAgents
        );
      })
      .catch(() => {
        if (isMounted) setAgentOptions(fallbackAgents);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    async function loadMonitoring() {
      setIsLoading(true);
      setLoadError("");

      try {
        const params = new URLSearchParams({ range });
        if (selectedAgentId !== "all") params.set("agentId", selectedAgentId);
        const data = await fetchFirstJson(`/api/reports/monitoring?${params}`, {
          signal: controller.signal,
        });

        if (!controller.signal.aborted) setMonitoring(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          setLoadError(error.message);
          setMonitoring(emptyMonitoring);
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    }

    loadMonitoring();
    return () => controller.abort();
  }, [range, selectedAgentId, refreshKey]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setRefreshKey((value) => value + 1);
    }, 10000);

    return () => window.clearInterval(interval);
  }, []);

  const hasMonitoringData = useMemo(() => {
    return (
      (monitoring.kpiCards || []).length > 0 ||
      (monitoring.trend || []).some((item) => item.sessions > 0 || item.aiHandled > 0) ||
      (monitoring.agentHealth || []).some((agent) => agent.volume > 0) ||
      (monitoring.alerts || []).length > 0 ||
      (monitoring.sessions || []).length > 0
    );
  }, [monitoring]);
  const dashboardData = hasMonitoringData ? monitoring : previewMonitoring;

  const outcomeData = useMemo(() => {
    const data = dashboardData.outcomeData || [];
    if (data.some((item) => item.value > 0)) return data;
    return [{ name: "No data", value: 1, percentLabel: "0%", color: "#d1d5db" }];
  }, [dashboardData.outcomeData]);
  const agentHealthRows = useMemo(() => {
    const rows = dashboardData.agentHealth || [];
    const cxRows = cxStudioSubAgents.map((agent) => ({
      id: agent.id,
      name: agent.name,
      status: agent.status,
      successRate: agent.successRate,
      latency: agent.latency,
      volume: agent.volume,
      tool: agent.tool,
    }));

    if (rows.length) {
      return [
        ...rows,
        ...cxRows.filter((cxAgent) => !rows.some((row) => row.id === cxAgent.id)),
      ];
    }

    const fallbackRows = agentOptions
      .filter((agent) => !lockAgent || agent.id === selectedAgentId)
      .map((agent) => ({
        id: agent.id,
        name: agent.name,
        status: "Waiting",
        successRate: "0%",
        latency: "--",
        volume: 0,
      }));

    return [...fallbackRows, ...cxRows];
  }, [agentOptions, dashboardData.agentHealth, lockAgent, selectedAgentId]);
  const displayedAgentRows = useMemo(
    () =>
      agentHealthRows.filter(
        (agent) =>
          hideAgentSelector ||
          !lockAgent ||
          selectedAgentId === "all" ||
          agent.id === selectedAgentId
      ),
    [agentHealthRows, hideAgentSelector, lockAgent, selectedAgentId]
  );
  const agentTotalSessions = displayedAgentRows.reduce(
    (total, agent) => total + (Number(agent.volume) || 0),
    0
  );
  const activeAgentCount = displayedAgentRows.filter(
    (agent) => ["healthy", "active"].includes(statusClass(agent.status))
  ).length;
  const configuredAgentCount = displayedAgentRows.length;
  const averageLatencyMs = useMemo(() => {
    const latencyValues = displayedAgentRows
      .map((agent) => Number(String(agent.latency || "").replace(/[^\d.]/g, "")))
      .filter((value) => Number.isFinite(value) && value > 0);

    if (!latencyValues.length) return null;
    return Math.round(
      latencyValues.reduce((total, value) => total + value, 0) / latencyValues.length
    );
  }, [displayedAgentRows]);
  const dashboardKpiCards = useMemo(() => {
    const transferCount = Math.max(
      0,
      Number((dashboardData.kpiCards || []).find((card) => card.label === "Transfers")?.value) || 0
    );
    const errorCount = Number(
      (dashboardData.kpiCards || []).find((card) => card.label === "Errors")?.value ||
        (dashboardData.alerts || []).filter((alert) => /error/i.test(alert.severity)).length
    );
    const handledCount = Math.max(0, agentTotalSessions - transferCount - errorCount);
    const handledPercent = agentTotalSessions
      ? Math.round((handledCount / agentTotalSessions) * 100)
      : 0;
    const transferPercent = agentTotalSessions
      ? Math.round((transferCount / agentTotalSessions) * 100)
      : 0;
    const errorPercent = agentTotalSessions
      ? Math.round((errorCount / agentTotalSessions) * 100)
      : 0;

    return [
      { label: "Sessions", value: String(agentTotalSessions), change: `${configuredAgentCount} agents`, tone: "good" },
      { label: "AI handled", value: String(handledCount), change: `${handledPercent}%`, tone: "good" },
      { label: "Transfers", value: String(transferCount), change: `${transferPercent}%`, tone: "neutral" },
      {
        label: "Avg latency",
        value: averageLatencyMs ? `${averageLatencyMs}ms` : "--",
        change: averageLatencyMs && averageLatencyMs <= 1500 ? "Healthy" : "Review",
        tone: averageLatencyMs && averageLatencyMs <= 1500 ? "good" : "warn",
      },
      { label: "Errors", value: String(errorCount), change: `${errorPercent}%`, tone: errorCount ? "warn" : "good" },
      {
        label: "Configured",
        value: `${activeAgentCount}/${configuredAgentCount}`,
        change: "Agents",
        tone: activeAgentCount === configuredAgentCount ? "good" : "warn",
      },
    ];
  }, [
    activeAgentCount,
    agentTotalSessions,
    averageLatencyMs,
    configuredAgentCount,
    dashboardData.alerts,
    dashboardData.kpiCards,
  ]);
  const dashboardTrend = useMemo(() => {
    const trend = dashboardData.trend || [];
    if (trend.some((item) => item.sessions > 0 || item.aiHandled > 0)) return trend;

    const labels = range === "today"
      ? ["9 AM", "10 AM", "11 AM", "12 PM", "1 PM", "2 PM"]
      : ["Jun 10", "Jun 11", "Jun 12", "Jun 13", "Jun 14", "Jun 15", "Jun 16"];
    const weights = labels.map((_, index) => index + 1);
    const weightTotal = weights.reduce((total, value) => total + value, 0);

    return labels.map((label, index) => {
      const sessions = Math.round((agentTotalSessions * weights[index]) / weightTotal);
      return {
        time: label,
        sessions,
        aiHandled: sessions,
      };
    });
  }, [agentTotalSessions, dashboardData.trend, range]);
  const dashboardOutcomeData = useMemo(() => {
    if (!agentTotalSessions) return outcomeData;

    return [
      { name: "AI handled", value: agentTotalSessions, percentLabel: "100%", color: "#2d7a45" },
      { name: "Error", value: 0, percentLabel: "0%", color: "#c0392b" },
      { name: "Human transfer", value: 0, percentLabel: "0%", color: "#b7791f" },
    ];
  }, [agentTotalSessions, outcomeData]);
  const latencyBreakdownData = useMemo(() => {
    const breakdown = normalizeLatencyBreakdown(dashboardData.latencyBreakdown || []);

    if (breakdown.length) return breakdown;
    return normalizeLatencyBreakdown(previewMonitoring.latencyBreakdown);
  }, [dashboardData.latencyBreakdown]);
  const dashboardSessions = useMemo(() => {
    const sessions = dashboardData.sessions || [];
    if (sessions.length && sessions.some((session) => !/Insurance - FNOL & Claims/i.test(session.agent || ""))) {
      return sessions;
    }

    return displayedAgentRows
      .filter((agent) => (Number(agent.volume) || 0) > 0)
      .slice(0, 6)
      .map((agent, index) => ({
        id: `AGT-${String(index + 1).padStart(3, "0")}`,
        agent: agent.name,
        state: "Handled",
      }));
  }, [dashboardData.sessions, displayedAgentRows]);
  const hasTrendData = (dashboardTrend || []).some(
    (item) => item.sessions > 0 || item.aiHandled > 0
  );
  const hasVolumeData = displayedAgentRows.some((agent) => agent.volume > 0);
  const hasLatencyBreakdownData = latencyBreakdownData.length > 0;

  return (
    <main className={`report-main ${className}`.trim()} aria-label={ariaLabel}>
      <div className="report-container">
        <div className="report-header-controls report-header-controls--inline">
          <span className={`status-message ${loadError ? "error" : ""}`}>
            {isLoading
              ? "Loading live metrics..."
              : loadError || (hasMonitoringData ? formatUpdatedAt(monitoring.generatedAt) : "Preview data")}
          </span>
          <div className="report-actions" aria-label="Analytics controls">
            <label className="range-select-wrap" htmlFor="analytics-range">
              <select
                id="analytics-range"
                className="range-select"
                value={range}
                onChange={(event) => setRange(event.target.value)}
                aria-label="Select analytics date range"
              >
                <option value="today">Today</option>
                <option value="7d">7D</option>
                <option value="15d">15D</option>
              </select>
              <i className="fas fa-chevron-down" aria-hidden="true"></i>
            </label>
            {!lockAgent && !hideAgentSelector && (
              <label className="range-select-wrap agent-select-wrap" htmlFor="analytics-agent">
                <select
                  id="analytics-agent"
                  className="range-select"
                  value={selectedAgentId}
                  onChange={(event) => setSelectedAgentId(event.target.value)}
                  aria-label="Select analytics agent"
                >
                  <option value="all">All agents</option>
                  {agentOptions.map((agent) => (
                    <option value={agent.id} key={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
                <i className="fas fa-chevron-down" aria-hidden="true"></i>
              </label>
            )}
            <button
              type="button"
              className="icon-action"
              aria-label="Refresh analytics data"
              onClick={() => setRefreshKey((value) => value + 1)}
              disabled={isLoading}
            >
              <i className={`fas fa-rotate-right ${isLoading ? "fa-spin" : ""}`}></i>
            </button>
          </div>
        </div>

        <section className="monitor-kpi-grid" aria-label="Monitoring summary">
          {dashboardKpiCards.map((card) => (
            <article className="monitor-kpi-card" key={card.label}>
              <span>{card.label}</span>
              <strong>{card.value}</strong>
              <em className={`kpi-change ${card.tone}`}>{card.change}</em>
            </article>
          ))}
        </section>

        <section className="monitor-layout">
          <article className="monitor-panel trend-panel">
            <div className="panel-heading">
              <h2>Session Trend</h2>
              <span>Sessions vs AI handled</span>
            </div>
            <div className="chart-frame">
              {hasTrendData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboardTrend} margin={{ top: 8, right: 10, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7decf" />
                    <XAxis dataKey="time" tick={{ fontSize: 11 }} stroke="#7e7e7e" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#7e7e7e" />
                    <Tooltip />
                    <Line type="monotone" dataKey="sessions" name="Sessions" stroke="#ffd28a" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="aiHandled" name="AI handled" stroke="#ff8a50" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="chart-empty">No live sessions captured yet</div>
              )}
            </div>
          </article>

          <article className="monitor-panel outcome-panel">
            <div className="panel-heading">
              <h2>Containment Mix</h2>
            </div>
            <div className="outcome-content">
              <ResponsiveContainer width="46%" height={140}>
                <PieChart>
                  <Pie data={dashboardOutcomeData} dataKey="value" innerRadius={38} outerRadius={62} paddingAngle={3}>
                    {dashboardOutcomeData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="outcome-legend">
                {dashboardOutcomeData.map((item) => (
                  <div className="legend-row" key={item.name}>
                    <span style={{ backgroundColor: item.color }}></span>
                    <strong>{item.name}</strong>
                    <em>{item.percentLabel}</em>
                  </div>
                ))}
              </div>
            </div>
          </article>

          <article className="monitor-panel latency-panel">
            <div className="panel-heading">
              <div>
                <h2>End to end latency breakdown</h2>
                <span>LLM and tool percentiles</span>
              </div>
              <a
                className="panel-link"
                href={CX_STUDIO_DASHBOARD_URL}
                target="_blank"
                rel="noreferrer"
              >
                CX Studio
              </a>
            </div>
            <div className="chart-frame latency-chart-frame">
              {hasLatencyBreakdownData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={latencyBreakdownData} margin={{ top: 8, right: 8, left: -6, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7decf" />
                    <XAxis dataKey="component" tick={{ fontSize: 11 }} stroke="#7e7e7e" />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      stroke="#7e7e7e"
                      tickFormatter={(value) => formatLatency(value)}
                    />
                    <Tooltip formatter={(value, name) => [formatLatency(value), String(name).toUpperCase()]} />
                    {latencyMetrics.map((metric) => (
                      <Bar
                        key={metric}
                        dataKey={metric}
                        name={metric.toUpperCase()}
                        fill={latencyMetricColors[metric]}
                        radius={[5, 5, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="chart-empty">Latency breakdown will appear after CX monitoring data is available</div>
              )}
            </div>
            <div className="latency-legend" aria-label="Latency metric legend">
              {latencyMetrics.map((metric) => (
                <span key={metric}>
                  <i style={{ backgroundColor: latencyMetricColors[metric] }}></i>
                  {metric.toUpperCase()}
                </span>
              ))}
            </div>
          </article>

          <article className="monitor-panel health-panel">
            <div className="panel-heading">
              <h2>Agent Health</h2>
              <span>Runtime and quality</span>
            </div>
            <div className="agent-health-table">
              <div className="table-row table-head">
                <span>Agent</span>
                <span>Status</span>
                <span>AI handled</span>
                <span>Latency</span>
                <span>Sessions</span>
              </div>
              {displayedAgentRows.map((agent) => (
                <div className="table-row" key={agent.id}>
                  <strong>{agent.name}</strong>
                  <span className={`status-pill ${statusClass(agent.status)}`}>{agent.status}</span>
                  <span>{agent.successRate}</span>
                  <span>{agent.latency}</span>
                  <span>{agent.volume}</span>
                </div>
              ))}
            </div>
          </article>

          <article className="monitor-panel bar-panel">
            <div className="panel-heading">
              <h2>Volume By Agent</h2>
              <span>Sessions handled</span>
            </div>
            <div className="chart-frame small">
              {hasVolumeData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={displayedAgentRows} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e7decf" />
                    <XAxis dataKey="name" hide />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#7e7e7e" />
                    <Tooltip />
                    <Bar dataKey="volume" name="Sessions" fill="#ff8a50" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="chart-empty">Agent volume will appear after the next chat</div>
              )}
            </div>
          </article>

          <article className="monitor-panel activity-panel">
            <div className="panel-heading">
              <h2>Live Activity</h2>
              <span>Alerts and sessions</span>
            </div>
            <div className="activity-grid">
              <div className="activity-block">
                <h3>Alerts</h3>
                <div className="alert-list">
                  {(dashboardData.alerts || []).length > 0 ? (
                    dashboardData.alerts.map((alert) => (
                      <div className="alert-row" key={`${alert.severity}-${alert.message}-${alert.time}`}>
                        <span className={`severity ${statusClass(alert.severity)}`}>{alert.severity}</span>
                        <strong>{alert.message}</strong>
                        <em>{alert.time}</em>
                      </div>
                    ))
                  ) : (
                    <div className="empty-row">No active alerts</div>
                  )}
                </div>
              </div>
              <div className="activity-block">
                <h3>Recent Sessions</h3>
                <div className="session-table">
                  {dashboardSessions.length > 0 ? (
                    dashboardSessions.map((session) => (
                      <div className="session-row" key={session.id}>
                        <strong>{session.id}</strong>
                        <span>{session.agent}</span>
                        <em>{session.state}</em>
                      </div>
                    ))
                  ) : (
                    <div className="empty-row">No sessions in this range</div>
                  )}
                </div>
              </div>
            </div>
          </article>
        </section>
      </div>
    </main>
  );
};

export default MonitoringDashboard;
